import {
  isKnownQuestionType,
  QuestionTypeEnum,
  SurveyQuestion,
} from '@employ-engage/data';
import * as Yup from 'yup';

/**
 * What a page of a survey requires before it can be submitted.
 *
 * Lives outside the component so the rule that matters most here can be tested
 * directly: a question this build cannot render must never be required. It has
 * no input, so a requirement on it can never be satisfied, and a survey with
 * one becomes permanently unsubmittable for every recipient — which is exactly
 * what a model inventing `"type": "rating"` used to produce.
 *
 * This is one half of a two-part fix. The other is the renderer's fallback
 * notice; either on its own still leaves the respondent stuck or confused.
 */
export const buildValidationSchema = (
  questions: SurveyQuestion[],
  t: (key: string) => string
) => {
  const answerSchemas: Yup.Schema<any>[] = (questions ?? []).map((question) => {
    const isRequired = question.isRequired !== false;

    if (!isRequired) {
      // nullable, because an unanswered numeric question stores null and
      // Yup's mixed() rejects it by default — which would make an optional
      // question impossible to leave blank.
      return Yup.mixed().nullable();
    }

    // See the note above: no input means no way to answer.
    if (!isKnownQuestionType(question.type)) {
      return Yup.mixed().nullable();
    }

    switch (question.type) {
      case QuestionTypeEnum.LONG_TEXT:
      case QuestionTypeEnum.SHORT_TEXT:
        return Yup.string().required(t('Required'));
      case QuestionTypeEnum.SELECT_MANY:
        return Yup.object({
          subAnswers: Yup.array()
            .of(Yup.mixed())
            .default([])
            .min(1, t('At least 1 Required')),
        });
      case QuestionTypeEnum.SELECT_ONE:
        return Yup.string().required(t('Required'));
      case QuestionTypeEnum.SCALE:
      case QuestionTypeEnum.NUMBER: {
        // A scale whose top is below its bottom renders no buttons, so
        // requiring an answer would make the page unsubmittable. The template
        // form refuses to save one; this is the backstop for anything already
        // stored, or imported.
        if (
          question.scale &&
          question.scale.max <= question.scale.min
        ) {
          return Yup.mixed().nullable();
        }
        // `mixed` with an explicit presence test, because Yup.number()
        // .required() rejects 0 — and 0 is a legitimate answer on a 0-10
        // scale and on any quantity.
        const scale = question.scale;
        return Yup.mixed()
          .nullable()
          .test(
            'answered',
            t('Required'),
            (value) => typeof value === 'number' && !Number.isNaN(value)
          )
          .test('in-range', t('Out of range'), (value) => {
            if (typeof value !== 'number' || !scale) return true;
            return value >= scale.min && value <= scale.max;
          });
      }
      case QuestionTypeEnum.DATE:
        // The browser's date input only ever emits 'YYYY-MM-DD' or '', but a
        // response can also arrive from an import, so the shape is checked
        // rather than assumed.
        return Yup.string()
          .required(t('Required'))
          .matches(/^\d{4}-\d{2}-\d{2}$/, t('Enter a date'));
      default:
        // A known type with no case here yet: `mixed` with a presence test, so
        // a legitimate `0` counts as answered where `Yup.string().required()`
        // would have rejected it.
        return Yup.mixed()
          .nullable()
          .test(
            'answered',
            t('Required'),
            (value) => value !== undefined && value !== null && value !== ''
          );
    }
  });

  return Yup.object().shape({
    answers:
      answerSchemas.length > 0
        ? Yup.tuple(
            answerSchemas as [Yup.Schema<any>, ...Yup.Schema<any>[]]
          ).required()
        : Yup.array().of(Yup.mixed()).required(),
  });
};
